import { usePointCloudContext } from 'features/pointClouds'
import { X } from 'lucide-react'
import { useObservationDialogue } from 'pages/pointClouds/components/observations/hooks/useObservationDialogue'
import { ActionButtons } from 'pages/pointClouds/components/observations/observationDialogue/ActionButtons'
import { ObservationDetailsForm } from 'pages/pointClouds/components/observations/observationDialogue/ObservationDetailsForm'
import { ObservationDialoguePositionForm } from 'pages/pointClouds/components/observations/observationDialogue/ObservationDialoguePositionForm'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { GeckoSiteObservation } from 'types'

import { ObservationImagePreview } from './ObservationImagePreview'

interface ObservationEditHudPanelProps {
  isOpen: boolean
  observation?: GeckoSiteObservation
}

const EditPanelContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 150px;
  left: 4px;
  width: 750px;
  padding: 20px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  color: white;
  overflow-y: auto;
  max-height: calc(80vh - 40px);

  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(-820px)'};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition:
    transform 200ms ease-out,
    opacity 200ms ease-out;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.4);
  }
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: white;
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &:active {
    background: rgba(255, 255, 255, 0.2);
  }
`

const labelStyle: React.CSSProperties = {
  color: '#aaa',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  width: '80px',
  textAlign: 'right',
}

const inputBaseStyle: React.CSSProperties = {
  backgroundColor: '#2a2a2a',
  border: '1px solid #444',
  borderRadius: '4px',
  color: '#ffffff',
  padding: '6px 10px',
  fontSize: '12px',
  flex: 1,
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
  gap: '8px',
}

const gridRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '80px minmax(150px, 1fr) 80px minmax(150px, 1fr)',
  alignItems: 'center',
  marginBottom: '8px',
  gap: '8px',
}

const threeColumnGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr 80px 1fr 80px 1fr',
  alignItems: 'center',
  marginBottom: '8px',
  gap: '8px',
}

const StyledDatePickerWrapper = styled.div`
  width: 100%;
  .ant-picker {
    width: 100%;
  }
  .ant-picker-input > input {
    font-size: 12px;
  }
  .ant-picker-input > input::placeholder {
    font-size: 12px;
    color: #888;
  }
`

export const ObservationEditHudPanel: React.FC<
  ObservationEditHudPanelProps
> = ({ isOpen, observation }) => {
  const {
    fileInputRef,
    isSaving,
    isDeleting,
    handleSave,
    handleImageUpload,
    handleRemoveImage,
    handleDelete,
    handleCloseDialogue: onClose,
    currentImages,
  } = useObservationDialogue({
    observation,
  })

  const { observationPosition } = usePointCloudContext()

  const [formData, setFormData] = useState<
    Partial<GeckoSiteObservation['data']>
  >({})

  useEffect(() => {
    if (observation?.data) {
      const dataToSet: Partial<GeckoSiteObservation['data']> = {
        severity: observation.data.severity,
        type: observation.data.type,
        status: observation.data.status,
        equipment: observation.data.equipment || '',
        area_id: observation.data.area_id || '',
        component: observation.data.component || '',
        name: observation.data.name || '',
        description: observation.data.description || '',
        date_found: observation.data.date_found,
        department: observation.data.department,
        notification_number: observation.data.notification_number || '',
        wo_number: observation.data.wo_number || '',
        pointCloudCoordinates: observation.data.pointCloudCoordinates,
      }
      setFormData(dataToSet)
    } else {
      if (!observationPosition) {
        console.error('Dialogue position is undefined')
        return
      }
      const defaultCoordinates = observationPosition?.toArray() as [
        number,
        number,
        number,
      ] //typscript struggling to infer this type - as cast for now
      setFormData({
        description: '',
        pointCloudCoordinates: { coordinate: defaultCoordinates },
      })
    }
  }, [observation, observationPosition])

  return (
    <>
      <EditPanelContainer $isOpen={isOpen}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
        <Title>
          {observation?.id ? 'Edit Observation' : 'Add Observation'}
        </Title>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '15px' }}>
            <ObservationDialoguePositionForm
              coordinates={formData.pointCloudCoordinates}
              setFormData={setFormData}
              disabled={isSaving || isDeleting}
              labelStyle={labelStyle}
              inputBaseStyle={inputBaseStyle}
              rowStyle={rowStyle}
            />
          </div>

          <input
            type='file'
            accept='image/*'
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            ref={fileInputRef}
            id='image-upload'
            multiple
          />

          <ObservationDetailsForm
            formData={formData}
            setFormData={setFormData}
            disabled={isSaving || isDeleting}
            labelStyle={labelStyle}
            inputBaseStyle={inputBaseStyle}
            rowStyle={rowStyle}
            gridRowStyle={gridRowStyle}
            threeColumnGridStyle={threeColumnGridStyle}
            StyledDatePickerWrapper={StyledDatePickerWrapper}
          />

          <ActionButtons
            observation={observation}
            formData={formData}
            isSaving={isSaving}
            isDeleting={isDeleting}
            handleSave={handleSave}
            handleDelete={handleDelete}
            handleImageUpload={handleImageUpload}
          />
        </div>
      </EditPanelContainer>

      <ObservationImagePreview
        isOpen={isOpen}
        images={currentImages}
        onRemove={handleRemoveImage}
      />
    </>
  )
}
